// Incoming "Talk now" prompt. Mounted once for the whole app (app.jsx). Renders
// nothing until the practice channel says a patient is asking to talk, then shows
// the instant video bar: the voice call pill's design, at the top of the screen,
// flashing, with the first name, a 45 s countdown and Accept / Dismiss.
// The first accept wins; everyone else's bar goes away when `taken` arrives.
//
// This file owns the queue and the API calls; InstantVideoPill draws them.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from '../../redux/store';
import useAuth from '../../hooks/useAuth';
import { consoleUrl } from '../pms/video/consoleUrl';
import { playRingTone, showDesktopNotification, soundOn, unlockRingTone } from '../callPill/callPillSync';
import InstantVideoPill from './InstantVideoPill';
import { ACCEPT_OUTCOMES, acceptInstantVideoRequest, declineInstantVideoRequest } from './instantVideoApi';
import { INSTANT_VIDEO_ENDED, INSTANT_VIDEO_RINGING, INSTANT_VIDEO_TAKEN, INSTANT_VIDEO_WINDOW_EVENT } from './instantVideoEvents';

export const RING_MS = 45000;
export const MUTE_KEY = 'instantVideoMuted';
const NOTICE_MS = 4000;

const readMuted = () => { try { return localStorage.getItem(MUTE_KEY) === '1'; } catch { return false; } };
// A soft two-note chime; browsers that block audio before a gesture simply stay silent.
const chime = () => {
    try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        [660, 880].forEach((freq, i) => {
            const osc = ctx.createOscillator(); const gain = ctx.createGain();
            osc.type = 'sine'; osc.frequency.value = freq; osc.connect(gain); gain.connect(ctx.destination);
            const t = ctx.currentTime + i * 0.18;
            gain.gain.setValueAtTime(0.0001, t); gain.gain.exponentialRampToValueAtTime(0.08, t + 0.03); gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
            osc.start(t); osc.stop(t + 0.4);
        });
        setTimeout(() => ctx.close?.(), 1000);
    } catch { /* silent */ }
};

export default function InstantVideoHost() {
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const navigate = useNavigate();
    const { user } = useAuth();
    const userIdRef = useRef(null);
    userIdRef.current = user?.pk ?? null;
    const [calls, setCalls] = useState([]); // { id, firstName, expiresAt, practiceId, state, notice }
    const [now, setNow] = useState(Date.now());
    const [muted, setMuted] = useState(readMuted);
    const mutedRef = useRef(muted);
    mutedRef.current = muted;

    // Phone and video ring the same way for anyone who has turned call sound on for
    // the voice pill, so the sound is learned once. Everyone else keeps the soft
    // chime, which needs no preference and no unlocking gesture.
    const ringSound = useCallback(() => {
        if (mutedRef.current) return;
        if (soundOn(userIdRef.current)) {
            unlockRingTone();
            if (playRingTone()) return;
        }
        chime();
    }, []);

    // A call that rings while the CRM is in a background tab or behind another window.
    // Handles are kept so the alert is closed the moment the call leaves the queue
    // (taken by a colleague, expired, accepted or dismissed here) instead of lingering.
    const notesRef = useRef(new Map());
    const notifyDesktop = useCallback((requestId, firstName) => {
        if (typeof window === 'undefined' || window.Notification?.permission !== 'granted') return;
        const handle = showDesktopNotification(
            { title: `${firstName || 'A patient'} wants to talk now`, body: 'Instant video call', tag: requestId },
            {
                onClick: () => {
                    // showDesktopNotification already focuses the window; put the caret on
                    // the Accept pill so the call can be taken with one keypress.
                    try { document.querySelector(`[data-testid="instant-video-accept-${requestId}"]`)?.focus(); } catch { /* gone */ }
                },
            },
        );
        if (handle) notesRef.current.set(requestId, handle);
    }, []);
    useEffect(() => {
        notesRef.current.forEach((handle, id) => {
            if (calls.some((c) => c.id === id)) return;
            try { handle.close(); } catch { /* already gone */ }
            notesRef.current.delete(id);
        });
    }, [calls]);
    useEffect(() => () => { notesRef.current.forEach((handle) => { try { handle.close(); } catch { /* ignore */ } }); notesRef.current.clear(); }, []);

    // Only this practice's calls ever show: a switch of practice clears the queue.
    useEffect(() => { setCalls([]); }, [practiceId]);

    useEffect(() => {
        const onEvent = (event) => {
            const msg = event.detail;
            if (!msg || !practiceId) return;
            if (msg.type === INSTANT_VIDEO_TAKEN || msg.type === INSTANT_VIDEO_ENDED) {
                setCalls((old) => old.filter((c) => c.id !== msg.request_id || c.state === 'accepting'));
                return;
            }
            if (msg.type !== INSTANT_VIDEO_RINGING) return;
            const expiresAt = msg.expires_at ? Date.parse(msg.expires_at) : Date.now() + (msg.seconds || RING_MS / 1000) * 1000;
            if (expiresAt <= Date.now()) return;
            setCalls((old) => {
                if (old.some((c) => c.id === msg.request_id)) return old;
                ringSound();
                notifyDesktop(msg.request_id, msg.first_name);
                return [...old, { id: msg.request_id, firstName: msg.first_name, expiresAt, practiceId, state: 'ringing' }];
            });
        };
        window.addEventListener(INSTANT_VIDEO_WINDOW_EVENT, onEvent);
        return () => window.removeEventListener(INSTANT_VIDEO_WINDOW_EVENT, onEvent);
    }, [practiceId, ringSound, notifyDesktop]);

    // One clock for the countdown rings; it only runs while a call is showing.
    const showing = calls.length > 0;
    useEffect(() => {
        if (!showing) return undefined;
        setNow(Date.now());
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, [showing]);
    useEffect(() => {
        setCalls((old) => {
            const next = old.filter((c) => c.state !== 'ringing' || c.expiresAt > now);
            return next.length === old.length ? old : next;
        });
    }, [now]);

    const remove = useCallback((id) => setCalls((old) => old.filter((c) => c.id !== id)), []);
    const patch = useCallback((id, change) => setCalls((old) => old.map((c) => (c.id === id ? { ...c, ...change } : c))), []);
    const noticeTimers = useRef({});
    useEffect(() => () => Object.values(noticeTimers.current).forEach(clearTimeout), []);

    const accept = useCallback(async (call) => {
        patch(call.id, { state: 'accepting', notice: null });
        let result;
        try { result = await acceptInstantVideoRequest(call.practiceId, call.id); } catch { result = { ok: false }; }
        if (result.ok) {
            remove(call.id);
            navigate(consoleUrl(result.appointmentId, { leadId: result.leadId, startAt: result.startAt, instant: true }));
            return;
        }
        const outcome = ACCEPT_OUTCOMES[result.outcome] || ACCEPT_OUTCOMES.other;
        if (outcome.remove) {
            patch(call.id, { state: 'taken', notice: outcome.message });
            noticeTimers.current[call.id] = setTimeout(() => remove(call.id), NOTICE_MS);
        } else {
            patch(call.id, { state: 'ringing', notice: outcome.message });
        }
    }, [navigate, patch, remove]);

    const dismiss = useCallback((call) => {
        remove(call.id);
        declineInstantVideoRequest(call.practiceId, call.id);
    }, [remove]);

    const toggleMute = () => {
        const next = !muted;
        setMuted(next);
        try { localStorage.setItem(MUTE_KEY, next ? '1' : '0'); } catch { /* ignore */ }
    };
    if (!showing) return null;
    return (
        <InstantVideoPill
            calls={calls}
            now={now}
            muted={muted}
            onAccept={accept}
            onDismiss={dismiss}
            onToggleMute={toggleMute}
        />
    );
}
